/**
 * AddPaymentRow — строка добавления нового транша в режиме инлайн-редактирования.
 * Поля: плановая дата (DatePicker) + плановая сумма (денежный input); подтверждение ✓ (синяя) / ✕ (отмена).
 * POST выполняется только по ✓ (кнопка «сохранить»). Число колонок совпадает с таблицей.
 */

import { useState } from 'react';
import { rubToMinor } from '../../lib/money';
import { fmtDate, todayYmd } from './lib/dates';
import { DatePicker } from './ui/DatePicker';
import { cx } from './ui/cx';
import { IconCheck, IconX } from './ui/icons';

export interface AddPaymentDraft {
  plannedDate: string;
  plannedAmountMinor: string;
  comment?: string;
  /** Необязательный факт: если менеджер сразу вносит поступившую оплату при создании транша. */
  actualDate?: string;
  paidAmountMinor?: string;
}

export interface AddPaymentRowProps {
  nextNumber: number;
  pending: boolean;
  onConfirm: (draft: AddPaymentDraft) => void;
  onCancel: () => void;
}

export function AddPaymentRow({ nextNumber, pending, onConfirm, onCancel }: AddPaymentRowProps): JSX.Element {
  const [date, setDate] = useState<string>(todayYmd());
  const [dateOpen, setDateOpen] = useState(false);
  const [amountText, setAmountText] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Необязательный факт оплаты прямо в строке добавления (когда деньги уже поступили).
  const [factDate, setFactDate] = useState<string>(todayYmd());
  const [factDateOpen, setFactDateOpen] = useState(false);
  const [factAmountText, setFactAmountText] = useState('');

  const confirm = (): void => {
    setError(null);
    let minor: string;
    try {
      minor = rubToMinor(amountText);
    } catch {
      setError('Некорректная сумма');
      return;
    }
    if (BigInt(minor) <= 0n) {
      setError('Сумма должна быть больше нуля');
      return;
    }
    // Факт указывать не обязательно: если менеджер уже получил оплату — вносим её сразу.
    let factMinor: string | null = null;
    if (factAmountText.trim()) {
      try {
        factMinor = rubToMinor(factAmountText);
      } catch {
        setError('Некорректная сумма факта');
        return;
      }
      if (BigInt(factMinor) <= 0n) {
        setError('Сумма факта должна быть больше нуля');
        return;
      }
      if (BigInt(factMinor) > BigInt(minor)) {
        setError('Факт не может превышать план');
        return;
      }
    }
    onConfirm({
      plannedDate: date,
      plannedAmountMinor: minor,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(factMinor ? { actualDate: factDate, paidAmountMinor: factMinor } : {}),
    });
  };

  return (
    <tr className="pw-row pw-row--draft">
      <td className="pw-td pw-td--seq">{nextNumber}</td>

      <td className="pw-td">
        <DatePicker
          value={date}
          open={dateOpen}
          onOpenChange={setDateOpen}
          onSelect={(ymd) => {
            if (ymd) setDate(ymd);
          }}
          trigger={
            <button type="button" className="pw-cell-edit pw-num" aria-label="Плановая дата нового транша">
              {fmtDate(date)}
            </button>
          }
        />
      </td>

      <td className="pw-td pw-td--right">
        <span className={cx('pw-money', error && 'pw-money--error')}>
          <input
            type="text"
            inputMode="decimal"
            className="pw-money__input pw-num"
            value={amountText}
            autoFocus
            placeholder="0"
            aria-label="Плановая сумма нового транша"
            aria-invalid={error !== null}
            onChange={(e) => {
              const v = e.target.value;
              if (/^[\d\s .,₽]*$/.test(v)) {
                setAmountText(v);
                setError(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirm();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
          />
          <span className="pw-money__suffix" aria-hidden>
            ₽
          </span>
        </span>
      </td>

      {/* Дата факт — всегда кликабельна (учитывается только если введена сумма факта) */}
      <td className="pw-td">
        <DatePicker
          value={factDate}
          open={factDateOpen}
          onOpenChange={setFactDateOpen}
          onSelect={(ymd) => {
            if (ymd) setFactDate(ymd);
          }}
          trigger={
            <button
              type="button"
              className={cx('pw-cell-edit pw-num', !factAmountText.trim() && 'pw-muted')}
              aria-label="Фактическая дата оплаты"
              title="Дата фактической оплаты (учитывается, если указана сумма факта)"
            >
              {fmtDate(factDate)}
            </button>
          }
        />
      </td>
      {/* Сумма факт — необязательная: если оплата уже поступила, вносим сразу при создании */}
      <td className="pw-td pw-td--right">
        <span className={cx('pw-money', error && factAmountText.trim() && 'pw-money--error')}>
          <input
            type="text"
            inputMode="decimal"
            className="pw-money__input pw-num"
            value={factAmountText}
            placeholder="0"
            aria-label="Фактически оплачено (если уже оплачено)"
            title="Если оплата уже поступила — укажите сумму факта, транш сразу станет оплаченным"
            onChange={(e) => {
              const v = e.target.value;
              if (/^[\d\s .,₽]*$/.test(v)) {
                setFactAmountText(v);
                setError(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirm();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
          />
          <span className="pw-money__suffix" aria-hidden>
            ₽
          </span>
        </span>
      </td>

      {/* Статус — подсказка «черновик» */}
      <td className="pw-td">
        <input
          type="text"
          className="pw-input pw-input--comment"
          value={comment}
          placeholder="Комментарий"
          aria-label="Комментарий нового транша"
          maxLength={500}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirm();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
        />
      </td>

      {/* Действия — подтверждение/отмена */}
      <td className="pw-td pw-td--actions">
        <span className="pw-draft-actions">
          <button
            type="button"
            className="pw-icon-btn pw-icon-btn--confirm"
            aria-label="Добавить транш"
            title="Добавить"
            disabled={pending}
            onClick={confirm}
          >
            <IconCheck size={16} />
          </button>
          <button
            type="button"
            className="pw-icon-btn pw-icon-btn--cancel"
            aria-label="Отменить добавление"
            title="Отмена"
            disabled={pending}
            onClick={onCancel}
          >
            <IconX size={16} />
          </button>
        </span>
        {error ? <span className="pw-draft-actions__err pw-visually-hidden">{error}</span> : null}
      </td>
    </tr>
  );
}
